import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {ArrowRight, BookOpen, Users, Sparkles} from 'lucide-react';

const Home = () => {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".fade-in-section");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const featuredArticles = [
    {
      title: "Osrey — The Complete Guide",
      slug: "osrey-complete-guide",
      excerpt: "Everything you need to know about Osrey and Osreys in one comprehensive guide. From basics to advanced concepts.",
      image: "https://images.unsplash.com/photo-1557683316-973673baf926?w=800&h=600&fit=crop",
      category: "Guide",
    },
    {
      title: "10 Things You Didn't Know About Osreys",
      slug: "10-things-about-osreys",
      excerpt: "Discover surprising facts about Osreys that will change how you think about Osrey and its applications.",
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=600&fit=crop",
      category: "Insights",
    },
    {
      title: "Why Osrey Is Growing in Popularity",
      slug: "osrey-growing-popularity",
      excerpt: "Explore the trends and factors driving the rapid adoption of Osrey across industries and communities.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
      category: "Trends",
    },
  ];

  const features = [
    {
      icon: BookOpen,
      title: "Comprehensive Guides",
      description: "Deep-dive articles about Osrey and Osreys covering every aspect you need to know.",
    },
    {
      icon: Users,
      title: "Community Driven",
      description: "Join a thriving community of Osrey enthusiasts sharing insights and experiences.",
    },
    {
      icon: Sparkles,
      title: "Latest Updates",
      description: "Stay informed with the newest developments in the world of Osrey and Osreys.",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0D12] via-[#1C2A35] to-[#0A0D12]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00D4AA] rounded-full filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-3xl animate-pulse delay-1000"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#00D4AA] via-[#F5F7FA] to-[#FF6B35] bg-clip-text text-transparent animate-gradient">
            Osrey & Osreys Explained
          </h1>
          <p className="text-xl md:text-2xl text-[#F5F7FA] mb-8 max-w-3xl mx-auto">
            Everything Osrey, Curated and Connected. Your ultimate resource for understanding the world of Osrey and Osreys.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/blog"
              className="px-8 py-4 bg-gradient-to-r from-[#00D4AA] to-[#FF6B35] text-white font-semibold rounded-lg hover:shadow-2xl hover:shadow-[#00D4AA]/30 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              Explore Articles
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/about"
              className="px-8 py-4 bg-[#1C2A35] text-[#F5F7FA] font-semibold rounded-lg border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300 hover:scale-105"
            >
              Learn About Osrey
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#F5F7FA]/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#00D4AA] rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-[#1C2A35] fade-in-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#F5F7FA] mb-6">
              What is Osrey?
            </h2>
            <p className="text-lg text-[#F5F7FA]/80 leading-relaxed mb-8">
              Osrey represents a revolutionary approach to modern connectivity and information sharing. Understanding Osrey and the broader concept of Osreys is essential for anyone looking to stay ahead in today's rapidly evolving digital landscape. Our platform brings together comprehensive guides, expert insights, and a vibrant community dedicated to exploring everything related to Osrey.
            </p>
            <p className="text-lg text-[#F5F7FA]/80 leading-relaxed">
              Whether you're new to Osrey or an experienced user of Osreys, our curated content helps you discover, learn, and connect with others who share your passion for this innovative field.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#0A0D12] fade-in-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 bg-[#1C2A35] rounded-xl border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-xl hover:shadow-[#00D4AA]/10"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#00D4AA] to-[#FF6B35] rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#F5F7FA] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#F5F7FA]/70">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles Section */}
      <section className="py-20 bg-[#1C2A35] fade-in-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#F5F7FA] mb-4">
              Featured Articles
            </h2>
            <p className="text-lg text-[#F5F7FA]/70">
              Explore our most popular content about Osrey and Osreys
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredArticles.map((article, index) => (
              <Link
                key={index}
                to={`/blog/${article.slug}`}
                className="group bg-[#0A0D12] rounded-xl overflow-hidden border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-xl hover:shadow-[#00D4AA]/10"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gradient-to-r from-[#00D4AA] to-[#FF6B35] text-white text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#F5F7FA] mb-3 group-hover:text-[#00D4AA] transition-colors duration-300">
                    {article.title}
                  </h3>
                  <p className="text-[#F5F7FA]/70 text-sm mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center text-[#00D4AA] text-sm font-medium">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#00D4AA] to-[#FF6B35] text-white font-semibold rounded-lg hover:shadow-2xl hover:shadow-[#00D4AA]/30 transition-all duration-300 hover:scale-105"
            >
              View All Articles
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home;